/** Map backend AI component drafts into RubricScoringInterface AIAnalysis shape. */

export interface DraftEvidence {
  quote: string;
  start_time: number;
  end_time: number;
  speaker: string;
}

export interface ComponentDraft {
  id: string;
  run_id: string;
  evaluation_id: string;
  component_id: string;
  component_code: string;
  proposed_level: string;
  evidence: DraftEvidence[];
  rationale: string;
  model_name: string;
  prompt_version: string;
  transcript_hash: string;
  created_at: string;
}

export interface MappedAiDraftAnalysis {
  suggested_score: number;
  suggested_level: string;
  confidence: number;
  key_observations: string[];
  rationale: string;
  video_highlights: Array<{
    timestamp: { start: number; end: number };
    description: string;
    significance: 'high' | 'medium' | 'low';
  }>;
  is_draft: true;
  draft_id: string;
  model_name: string;
  prompt_version: string;
}

const LEVEL_SCORES: Record<string, number> = {
  ineffective: 1,
  developing: 2,
  effective: 3,
  highly_effective: 4,
};

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function draftToAiAnalysis(draft: ComponentDraft): MappedAiDraftAnalysis {
  const suggested_score = LEVEL_SCORES[draft.proposed_level] ?? 2;
  return {
    suggested_score,
    suggested_level: draft.proposed_level,
    confidence: 0.75,
    key_observations: draft.evidence.map(
      item =>
        `"${item.quote}" (${formatTime(item.start_time)}–${formatTime(item.end_time)}, ${item.speaker})`
    ),
    rationale: draft.rationale,
    video_highlights: draft.evidence.map(item => ({
      timestamp: { start: item.start_time, end: item.end_time },
      description: item.quote,
      significance: 'high' as const,
    })),
    is_draft: true,
    draft_id: draft.id,
    model_name: draft.model_name,
    prompt_version: draft.prompt_version,
  };
}

export function draftsByComponentId(
  drafts: ComponentDraft[]
): Record<string, MappedAiDraftAnalysis> {
  return Object.fromEntries(
    drafts.map(draft => [draft.component_id, draftToAiAnalysis(draft)])
  );
}
